/**
 * Accept Employee Invite Page
 *
 * Handles employee invite acceptance flow. Users arrive with a token in the URL,
 * verify the invite, set their password, and complete account activation.
 *
 * Issue #296 - Employee Invite Flow
 */

import { zodResolver } from '@hookform/resolvers/zod';
import CheckIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Box,
  CircularProgress,
  GlassAlert,
  GlassButton,
  GlassIconButton,
  GlassSelect,
  type GlassSelectOption,
  GlassTextField,
  InputAdornment,
  ShimmerLoader,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import env from '../config/env';
import { useTheme } from '../theme/ThemeProvider';

// Common timezone options
const TIMEZONES: GlassSelectOption[] = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York', label: 'Eastern Time (US)' },
  { value: 'America/Chicago', label: 'Central Time (US)' },
  { value: 'America/Denver', label: 'Mountain Time (US)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
];

interface InviteInfo {
  email: string;
  role: string;
  inviterName: string;
  businessName?: string;
  expiresAt: string;
}

export default function AcceptInvitePage() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { setBusinessId } = useTheme();

  // Password requirements
  const passwordSchema = z
    .string()
    .min(8, t('validation.minLength', { min: 8 }))
    .regex(/[A-Z]/, t('auth.acceptInvite.passwordUppercase'))
    .regex(/[a-z]/, t('auth.acceptInvite.passwordLowercase'))
    .regex(/[0-9]/, t('auth.acceptInvite.passwordNumber'));

  const acceptInviteSchema = z
    .object({
      password: passwordSchema,
      confirmPassword: z.string(),
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      phoneNumber: z.string().optional(),
      timezone: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('validation.passwordMismatch'),
      path: ['confirmPassword'],
    });

  type AcceptInviteFormData = z.infer<typeof acceptInviteSchema>;

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<AcceptInviteFormData>({
    resolver: zodResolver(acceptInviteSchema),
    defaultValues: {
      timezone: 'UTC',
    },
  });

  // Load invite info on mount
  useEffect(() => {
    const loadInviteInfo = async () => {
      if (!token) {
        setError(t('auth.acceptInvite.invalidLinkError'));
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${env.VITE_API_URL}/auth/employee-invite/${token}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(
            typeof data.message === 'string'
              ? data.message
              : data.message?.message || t('auth.acceptInvite.invalidOrExpired'),
          );
        }

        const data = await response.json();
        setInviteInfo({
          email: data.invite.email,
          role: data.invite.role,
          inviterName: data.inviter_name,
          businessName: data.business_name,
          expiresAt: data.invite.expires_at,
        });

        // Trigger branding fetch for white-label logo/colors
        if (data.business_id) {
          setBusinessId(data.business_id);
        }

        // Pre-fill first/last name from user profile if available
        if (data.first_name) setValue('firstName', data.first_name);
        if (data.last_name) setValue('lastName', data.last_name);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('auth.acceptInvite.failedToLoad'));
      } finally {
        setIsLoading(false);
      }
    };

    loadInviteInfo();
  }, [token, setValue, setBusinessId]);

  const onSubmit = async (data: AcceptInviteFormData) => {
    if (!token) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${env.VITE_API_URL}/auth/employee-invite/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          password: data.password,
          first_name: data.firstName,
          last_name: data.lastName,
          phone_number: data.phoneNumber,
          timezone: data.timezone,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          typeof errorData.message === 'string'
            ? errorData.message
            : errorData.message?.message || t('auth.acceptInvite.failedToAccept'),
        );
      }

      setSuccess(true);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        window.location.href = `${env.VITE_AUTH_PORTAL_URL}/login?email=${encodeURIComponent(inviteInfo?.email || '')}`;
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.acceptInvite.failedToAccept'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format role for display
  const formatRole = (role: string) => {
    const roleMap: Record<string, string> = {
      business_admin: t('auth.acceptInvite.roleAdmin'),
      business_user: t('auth.acceptInvite.roleUser'),
      business_viewer: t('auth.acceptInvite.roleViewer'),
      business_manager: t('auth.acceptInvite.roleManager'),
    };
    return roleMap[role] || role;
  };

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <ShimmerLoader width="100%" height={200} />
        <Typography variant="body2" color="textSecondary">
          {t('auth.acceptInvite.loadingInvite')}
        </Typography>
      </Box>
    );
  }

  // Error state (no valid invite)
  if (error && !inviteInfo) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          {t('auth.acceptInvite.invalidInvite')}
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
          {error}
        </Typography>
        <RouterLink to="/login" style={{ textDecoration: 'none' }}>
          <GlassButton variant="outlined">{t('auth.acceptInvite.goToLogin')}</GlassButton>
        </RouterLink>
      </Box>
    );
  }

  // Success state
  if (success) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          {t('auth.acceptInvite.successTitle')}
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
          {t('auth.acceptInvite.successDescription')}
        </Typography>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {inviteInfo?.businessName
            ? t('auth.acceptInvite.welcomeBusiness', { businessName: inviteInfo.businessName })
            : t('auth.acceptInvite.welcomeTeam')}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {inviteInfo?.businessName
            ? t('auth.acceptInvite.invitedBy', {
                inviterName: inviteInfo?.inviterName,
                teamName: inviteInfo.businessName,
                role: formatRole(inviteInfo?.role || ''),
              })
            : t('auth.acceptInvite.invitedByTeam', {
                inviterName: inviteInfo?.inviterName,
                role: formatRole(inviteInfo?.role || ''),
              })}
        </Typography>
      </Box>

      {/* Invite Info Card */}
      <Box
        sx={{
          bgcolor: 'action.hover',
          borderRadius: 2,
          p: 2,
          mb: 3,
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('common.email')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {inviteInfo?.email}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {t('common.role')}
        </Typography>
        <Typography variant="body1">{formatRole(inviteInfo?.role || '')}</Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <GlassAlert severity="error" visible sx={{ mb: 2 }}>
          {error}
        </GlassAlert>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Password */}
          <GlassTextField
            fullWidth
            label={t('common.password')}
            type={showPassword ? 'text' : 'password'}
            {...register('password')}
            error={!!errors.password}
            helperText={errors.password?.message}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <GlassIconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </GlassIconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          {/* Confirm Password */}
          <GlassTextField
            fullWidth
            label={t('auth.register.confirmPassword')}
            type={showConfirmPassword ? 'text' : 'password'}
            {...register('confirmPassword')}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <GlassIconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </GlassIconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          {/* Optional Profile Fields */}
          <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>
            {t('auth.acceptInvite.profileInfoOptional')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <GlassTextField
              fullWidth
              label={t('common.firstName')}
              {...register('firstName')}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
            />
            <GlassTextField
              fullWidth
              label={t('common.lastName')}
              {...register('lastName')}
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
            />
          </Box>

          <GlassTextField
            fullWidth
            label={t('common.phone')}
            {...register('phoneNumber')}
            placeholder="+1 (555) 123-4567"
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber?.message}
          />

          <Controller
            name="timezone"
            control={control}
            render={({ field }) => (
              <GlassSelect
                label={t('common.timezone')}
                value={field.value}
                onChange={field.onChange}
                options={TIMEZONES}
                fullWidth
              />
            )}
          />

          {/* Submit Button */}
          <GlassButton type="submit" variant="gradient" fullWidth disabled={isSubmitting} sx={{ mt: 2 }}>
            {isSubmitting ? <CircularProgress size={24} /> : t('auth.acceptInvite.activateAccount')}
          </GlassButton>
        </Box>
      </form>

      {/* Password Requirements */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="textSecondary">
          {t('auth.acceptInvite.passwordMust')}
        </Typography>
        <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
          <Typography component="li" variant="caption" color="textSecondary">
            {t('auth.acceptInvite.passwordMinLength')}
          </Typography>
          <Typography component="li" variant="caption" color="textSecondary">
            {t('auth.acceptInvite.passwordUppercase')}
          </Typography>
          <Typography component="li" variant="caption" color="textSecondary">
            {t('auth.acceptInvite.passwordLowercase')}
          </Typography>
          <Typography component="li" variant="caption" color="textSecondary">
            {t('auth.acceptInvite.passwordNumber')}
          </Typography>
        </ul>
      </Box>
    </Box>
  );
}
